import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { CircularProgress, useMediaQuery } from '@mui/material';
import Popup from './Popup';
import { mobileMaxWidthMediaQuery } from '../methods/utils';
import { setupPdfWorker } from '../utils/pdfWorkerSetup';

type Props = {
    pdfUrl: string;
    fileName?: string;
    filename?: string;
    onClose: () => void;
    onDownload?: () => void;
}

// Новый компонент, который рендерится через портал
function PdfViewerModal({ pdfUrl, fileName, filename, onClose, onDownload }: Props) {
    const [numPages, setNumPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pdfFile, setPdfFile] = useState<File | null>(null);
    const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
    const [documentWidth, setDocumentWidth] = useState<number | null>(null);
    const mobile = useMediaQuery(mobileMaxWidthMediaQuery);

    useEffect(() => {
        setupPdfWorker(pdfjs);
    }, []);

    useEffect(() => {
        if (pdfUrl) {
            setLoading(true);
            setError(null);
            try {
                if (pdfUrl.startsWith('data:application/pdf;base64,')) {
                    const base64Data = pdfUrl.split(',')[1];
                    if (!base64Data) throw new Error('Некорректные base64 данные');
                    const byteCharacters = atob(base64Data);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const file = new File([byteArray], filename || fileName || 'document.pdf', { type: 'application/pdf' });
                    setPdfFile(file);
                    const objectUrl = URL.createObjectURL(file);
                    setPdfObjectUrl(objectUrl);
                    setLoading(false);
                } else if (pdfUrl.startsWith('data:')) {
                    fetch(pdfUrl)
                        .then(res => res.blob())
                        .then(blob => {
                            const file = new File([blob], filename || fileName || 'document.pdf', { type: 'application/pdf' });
                            setPdfFile(file);
                            const objectUrl = URL.createObjectURL(file);
                            setPdfObjectUrl(objectUrl);
                            setLoading(false);
                        })
                        .catch(() => {
                            setError('Не удалось обработать PDF документ');
                            setLoading(false);
                        });
                } else {
                    fetch(pdfUrl)
                        .then(res => res.blob())
                        .then(blob => {
                            const file = new File([blob], filename || fileName || 'document.pdf', { type: 'application/pdf' });
                            setPdfFile(file);
                            const objectUrl = URL.createObjectURL(file);
                            setPdfObjectUrl(objectUrl);
                            setLoading(false);
                        })
                        .catch(() => {
                            setError('Не удалось загрузить PDF документ');
                            setLoading(false);
                        });
                }
            } catch {
                setError('Не удалось обработать PDF документ');
                setLoading(false);
            }
        } else {
            setError('PDF данные не предоставлены');
            setLoading(false);
        }
    }, [pdfUrl, fileName, filename]);

    useEffect(() => {
        return () => {
            if (pdfObjectUrl) {
                URL.revokeObjectURL(pdfObjectUrl);
            }
        };
    }, [pdfObjectUrl]);

    const handleLoadSuccess = (result: any) => {
        setNumPages(result.numPages);
        setLoading(false);
        setError(null);
    };

    const handleLoadError = (error: any) => {
        let errorMessage = 'Не удалось загрузить PDF документ';
        if (error.message) {
            if (error.message.includes('Invalid PDF')) {
                errorMessage = 'Файл поврежден или не является PDF документом';
            } else if (error.message.includes('worker')) {
                errorMessage = 'Ошибка обработки PDF. Попробуйте обновить страницу';
            } else if (error.message.includes('network')) {
                errorMessage = 'Ошибка сети при загрузке PDF';
            }
        }
        setError(errorMessage);
        setLoading(false);
    };

    const pages = Array.from(Array(numPages).keys());
    let width = mobile ? window.innerWidth - 32 : undefined;

    // Вычисляем ширину попапа на основе ширины документа
    const getPopupWidth = (): string | undefined => {
        if (mobile) return undefined; // На мобильных используем стандартную ширину
        if (documentWidth) {
            // Добавляем отступы для заголовка и кнопок
            const totalWidth = documentWidth + 60; // 30px отступы с каждой стороны
            const maxWidth = window.innerWidth * 0.9; // Максимум 90% экрана
            const minWidth = 600; // Минимум 600px для десктопа
            return `${Math.max(minWidth, Math.min(totalWidth, maxWidth))}px`;
        }
        return "60vw"; // Значение по умолчанию
    };

    // Рендерим через портал в body
    return createPortal(
        <Popup onClose={onClose} padding="15px" maxWidth={getPopupWidth()} style={{zIndex: 9999}}>
            <div>
                <div style={{margin: '0 30px' }}>
                    <div className="commonTitle" style={{textAlign: 'left'}}>
                        {filename || fileName || 'PDF документ'}
                    </div>
                    {!loading && !error && pdfFile && (
                        <button 
                            style={{ marginTop: "10px" }}
                            className="new withTitle small secondary"
                            onClick={() => {
                                if (onDownload) {
                                    onDownload();
                                } else if (pdfObjectUrl) {
                                    const link = document.createElement('a');
                                    link.href = pdfObjectUrl;
                                    link.download = filename || fileName || 'document.pdf';
                                    link.click();
                                }
                            }}
                        >
                            Скачать
                        </button>
                    )}
                </div>
                
                {loading && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <CircularProgress sx={{ color: "#1FA3EE" }} />
                        <div style={{ marginTop: '10px' }}>Загрузка документа...</div>
                    </div>
                )}

                {error && (
                    <div style={{ color: 'red', marginTop: 20 }}>
                        <b>Ошибка загрузки PDF:</b> {error}
                    </div>
                )}

                {!loading && !error && pdfFile && pdfObjectUrl && (
                    <div>
                        <Document
                            file={pdfObjectUrl}
                            onLoadSuccess={handleLoadSuccess}
                            onLoadError={handleLoadError}
                            noData={'Не удалось отобразить PDF'}
                            error={'Не удалось отобразить PDF'}
                            loading={
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <CircularProgress sx={{ color: "#1FA3EE" }} />
                                    <div style={{ marginTop: '10px' }}>Обработка PDF...</div>
                                </div>
                            }
                        >
                            {pages.map((i) => (
                                <div key={i} style={{ marginBottom: "15px", margin: '0 auto', width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <Page
                                        pageNumber={i + 1}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false}
                                        width={width}
                                        onLoadSuccess={(page) => {
                                            // Сохраняем ширину первой страницы для вычисления ширины попапа
                                            if (i === 0 && page.width) {
                                                setDocumentWidth(page.width);
                                            }
                                        }}
                                    />
                                </div>
                            ))}
                        </Document>
                    </div>
                )}
            </div>
        </Popup>,
        document.body
    );
}

// Оставляем старый компонент для обратной совместимости
function PdfViewer({ pdfUrl, fileName, filename, onClose, onDownload }: Props) {
    return <PdfViewerModal pdfUrl={pdfUrl} fileName={fileName} filename={filename} onClose={onClose} onDownload={onDownload} />;
}

export default PdfViewer;
export { PdfViewerModal }; 